import React, { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, Save, CheckCircle2, XCircle, Beaker, CreditCard } from "lucide-react";

const PROVIDERS = {
  stripe: {
    label: "Stripe",
    // Keys saved here were read by NO Stripe money path (the gateway reads env,
    // hydrated by the Integrations editor) — the form displayed "configured" while
    // Stripe stayed in demo mode. The card now points to the real store.
    managedElsewhere: true,
    fields: [],
    docs: "https://dashboard.stripe.com/apikeys",
  },
  paypal: {
    label: "PayPal",
    fields: [
      { key: "client_id", label: "Client ID", placeholder: "AX..." },
      { key: "client_secret", label: "Client Secret", placeholder: "EM...", sensitive: true },
      { key: "webhook_id", label: "Webhook ID", placeholder: "0XX..." },
    ],
    docs: "https://developer.paypal.com/dashboard/applications",
  },
};

export default function PaymentProvidersEditor() {
  const t = useT("superadmin");
  // Provider settings and their test are settings.write; the blocked payment methods, read and
  // write, are super admin only, so they are neither read nor offered to anyone else.
  const { providers: canEditProviders, methodPolicy: canSeePolicy } = useScreenWrites("payments");
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [policy, setPolicy] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // The payment-method policy is a separate read: its failure is shown in its panel's place and
  // the providers below keep working.
  const [policyError, setPolicyError] = useState(null);

  const load = async () => {
    setLoadError(null);
    try {
      const r = await api.get("/super-admin/payments/providers");
      setData(r.data);
    } catch (e) { setLoadError(e); }
  };
  const loadPolicy = async () => {
    setPolicyError(null);
    try { const r = await api.get("/super-admin/payment-methods"); setPolicy(r.data); }
    catch (e) { setPolicyError(e); }
  };
  useEffect(() => {
    load();
    if (canSeePolicy) loadPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeePolicy]);

  const toggleBlock = async (methodKey) => {
    const current = new Set(policy?.blocked || []);
    if (current.has(methodKey)) current.delete(methodKey); else current.add(methodKey);
    const blocked = Array.from(current);
    try {
      await api.put("/super-admin/payment-methods", { blocked });
      setPolicy({ ...policy, blocked });
      toast.success(t("pp.policyUpdated"));
    } catch (e) { toast.error(extractApiError(e, t("pp.error"), t)); }
  };

  if (loadError) return <PageLoadError error={loadError} onRetry={load} testId="payment-providers-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  const save = async (provider, mode, enabled) => {
    setSaving(provider);
    try {
      const credentials = {};
      PROVIDERS[provider].fields.forEach((f) => {
        const v = drafts[`${provider}.${f.key}`];
        if (v?.trim()) credentials[f.key] = v.trim();
      });
      if (Object.keys(credentials).length === 0) {
        toast.error(t("pp.fillOne")); setSaving(null); return;
      }
      await api.put("/super-admin/payments/providers",
        { provider, mode, enabled, credentials });
      toast.success(t("pp.savedProvider", { label: PROVIDERS[provider].label }));
      load();
    } catch (e) { toast.error(extractApiError(e, t("pp.error"), t)); }
    finally { setSaving(null); }
  };

  const test = async (provider) => {
    setTesting(provider);
    try {
      const r = await api.post(`/super-admin/payments/providers/${provider}/test`);
      setTestResults({ ...testResults, [provider]: r.data });
      if (r.data.valid) toast.success(t("pp.connValid", { label: PROVIDERS[provider].label }));
      else toast.error(t("pp.connInvalid", { label: PROVIDERS[provider].label, error: r.data.error || t("pp.invalid") }));
    } catch (e) { toast.error(t("pp.testError")); }
    finally { setTesting(null); }
  };

  return (
    <div data-testid="payment-providers">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">
        {t("pp.title")}
      </h1>
      <p className="text-slate-500 mb-8">{t("pp.subtitle")}</p>

      {canSeePolicy && policyError && (
        <div className="mb-6"><PageLoadError error={policyError} onRetry={loadPolicy} testId="payment-methods-policy-load-error" /></div>
      )}
      {canSeePolicy && policy && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-6" data-testid="payment-methods-policy">
          <h2 className="font-sans font-semibold text-xl mb-1">{t("pp.allowedMethods")}</h2>
          <p className="text-slate-400 text-sm mb-4">{t("pp.allowedDesc")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["stripe", t("pp.methodStripe")], ["stripe_connect", t("pp.methodStripeConnect")], ["paypal", t("pp.methodPaypal")]].map(([k, label]) => {
              const blocked = (policy.blocked || []).includes(k);
              return (
                <div key={k} className={`flex items-center justify-between rounded-xl border px-4 py-3 ${blocked ? "border-rose-400/40 bg-rose-500/5" : "border-slate-200"}`} data-testid={`method-policy-${k}`}>
                  <span className="text-sm text-slate-700">{label}</span>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <span className={blocked ? "text-rose-600" : "text-emerald-600"}>{blocked ? t("pp.blocked") : t("pp.allowed")}</span>
                    <input type="checkbox" checked={!blocked} onChange={() => toggleBlock(k)} data-testid={`toggle-block-${k}`} />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(PROVIDERS).map(([key, meta]) => {
          const status = data.providers[key];
          const result = testResults[key];
          const enabled = status?.enabled || false;
          const mode = status?.mode || "sandbox";
          return (
            <div key={key} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`provider-${key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <div>
                    <div className="font-sans font-semibold text-xl">{meta.label}</div>
                    <a href={meta.docs} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-amber-600">
                      {t("pp.getKeys")}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {result && (result.valid ?
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {t("pp.connValidShort")}</span> :
                    <span className="inline-flex items-center gap-1.5 text-xs text-rose-600"><XCircle className="w-4 h-4" /> {result.error?.slice(0, 60)}</span>
                  )}
                  <span className={`text-xs uppercase tracking-wider font-medium ${enabled ? (status.configured ? "text-emerald-400" : "text-amber-600") : "text-slate-400"}`}>
                    {enabled ? (status.configured ? t("pp.statusConfigured") : t("pp.statusEnabled")) : t("pp.statusInactive")}
                  </span>
                </div>
              </div>

              {meta.managedElsewhere ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3" data-testid={`managed-elsewhere-${key}`}>
                  <span>{t("pp.stripeManagedInIntegrations")}</span>
                  <a href="/super-admin/integrations" className="shrink-0 text-xs font-medium underline hover:text-amber-900">
                    {t("pp.openIntegrations")}
                  </a>
                </div>
              ) : canEditProviders && (
              <>
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                {meta.fields.map((f) => (
                  <div key={f.key}>
                    <label className="text-xs text-slate-500 mb-1 block">{f.label}</label>
                    <Input
                      type={f.sensitive ? "password" : "text"}
                      placeholder={status?.credentials_keys?.includes(f.key) ? t("pp.currentMasked") : f.placeholder}
                      value={drafts[`${key}.${f.key}`] || ""}
                      onChange={(e) => setDrafts({ ...drafts, [`${key}.${f.key}`]: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 shadow-sm"
                      data-testid={`input-${key}-${f.key}`}
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <select defaultValue={mode}
                  onChange={(e) => save(key, e.target.value, enabled)}
                  className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-800 shadow-sm"
                  data-testid={`mode-${key}`}>
                  <option value="sandbox" className="bg-white">{t("pp.sandbox")}</option>
                  <option value="production" className="bg-white">{t("pp.production")}</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-800/70 cursor-pointer">
                  <input type="checkbox" checked={enabled}
                    onChange={(e) => save(key, mode, e.target.checked)}
                    data-testid={`enable-${key}`} />
                  {t("pp.enabled")}
                </label>
                <div className="flex-1" />
                <Button onClick={() => test(key)} disabled={testing === key || !status?.configured}
                  variant="outline" className="border-slate-200 text-slate-800/70" data-testid={`test-${key}`}>
                  {testing === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Beaker className="w-4 h-4 mr-2" /> {t("pp.test")}</>}
                </Button>
                <Button onClick={() => save(key, mode, enabled)} disabled={saving === key}
                  className="bg-sol-gold text-black hover:bg-amber-200" data-testid={`save-${key}`}>
                  {saving === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t("pp.save")}</>}
                </Button>
              </div>
              </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
